<template>
  <b-modal
    v-model="open"
    size="xl"
    title="Файлы"
    body-class="file-library__body"
    :ok-title="multiple ? `Выбрать (${picked.length})` : 'Выбрать'"
    cancel-title="Отмена"
    :ok-disabled="!picked.length"
    @ok="confirm"
    @hidden="$emit('close')"
  >
    <div class="file-library__tools">
      <b-form-input
        v-model="search"
        type="search"
        placeholder="Поиск по названию файла"
        debounce="350"
        class="file-library__search"
        autofocus
      />
      <b-form-radio-group
        v-if="!imagesOnly"
        v-model="type"
        :options="TYPES"
        buttons
        button-variant="outline-primary"
        size="sm"
      />
    </div>

    <p
      v-if="error"
      class="file-library__state text-danger"
    >
      {{ error }}
      <b-button
        size="sm"
        variant="outline-danger"
        class="ml-1"
        @click="reload"
      >
        Повторить
      </b-button>
    </p>
    <p
      v-else-if="!loading && !files.length"
      class="file-library__state"
    >
      {{ search ? 'Ничего не найдено. Файлы, загруженные до появления этого списка, хранятся под служебным именем — их можно найти только без поиска, по дате.' : 'Здесь появятся файлы, загруженные через админку.' }}
    </p>

    <div
      v-if="files.length"
      class="file-library__grid"
    >
      <button
        v-for="file in files"
        :key="file.id"
        type="button"
        class="file-library__tile"
        :class="{ 'is-picked': isPicked(file) }"
        :aria-pressed="isPicked(file) ? 'true' : 'false'"
        :title="displayName(file)"
        @click="toggle(file)"
        @dblclick="pickNow(file)"
      >
        <span class="file-library__preview">
          <img
            v-if="isImage(file)"
            :src="file.fullPath"
            :alt="displayName(file)"
            loading="lazy"
          >
          <span
            v-else
            class="file-library__icon"
          >
            <feather-icon
              icon="FileTextIcon"
              size="30"
            />
            <small>{{ file.extension }}</small>
          </span>
          <span
            v-if="isPicked(file)"
            class="file-library__check"
          >
            <feather-icon
              icon="CheckIcon"
              size="14"
            />
          </span>
        </span>
        <span class="file-library__name">{{ displayName(file) }}</span>
        <span class="file-library__meta">{{ meta(file) }}</span>
      </button>
    </div>

    <div class="file-library__more">
      <b-spinner
        v-if="loading"
        small
      />
      <b-button
        v-else-if="files.length < count"
        variant="outline-primary"
        size="sm"
        @click="load"
      >
        Показать ещё ({{ count - files.length }})
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import {
  BButton, BFormInput, BFormRadioGroup, BModal, BSpinner,
} from 'bootstrap-vue'
import {
  describeUploadError, fileUrl, formatBytes, isImageName,
} from './upload'

const PAGE = 40

/**
 * The admin's file library (`GET /file/library`): the files uploaded through the admin, newest first, found by the
 * name they were uploaded with. Pick one (or several with `multiple`) — `@pick` gives the file objects. A double click
 * picks at once.
 */
export default {
  name: 'FileLibraryModal',
  components: {
    BButton, BFormInput, BFormRadioGroup, BModal, BSpinner,
  },
  props: {
    multiple: { type: Boolean, default: false },
    /** only pictures are offered (the uploader accepts nothing else) */
    imagesOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      open: true,
      search: '',
      type: 'all',
      files: [],
      count: 0,
      loading: false,
      error: '',
      picked: [],
      /** answers of an older search must not land in the list of a newer one */
      request: 0,
      TYPES: [
        { text: 'Все', value: 'all' },
        { text: 'Изображения', value: 'image' },
        { text: 'Документы', value: 'document' },
      ],
    }
  },
  watch: {
    search() {
      this.reload()
    },
    type() {
      this.reload()
    },
  },
  created() {
    this.load()
  },
  methods: {
    reload() {
      this.files = []
      this.count = 0
      this.load()
    },
    async load() {
      this.request += 1
      const current = this.request
      this.loading = true
      this.error = ''
      try {
        const { data } = await this.$http.get('/file/library', {
          silentError: true,
          params: {
            search: this.search.trim() || undefined,
            type: this.imagesOnly ? 'image' : this.type,
            skipCount: this.files.length,
            maxResultCount: PAGE,
          },
        })
        if (current !== this.request) return
        this.files = [...this.files, ...(data.rows || [])]
        this.count = Number(data.count) || this.files.length
      } catch (error) {
        if (current !== this.request) return
        this.error = describeUploadError(error, 'Не удалось загрузить список файлов')
      } finally {
        if (current === this.request) this.loading = false
      }
    },
    displayName(file) {
      return file.original_name || file.name
    },
    isImage(file) {
      return isImageName(file.name) || isImageName(fileUrl(file))
    },
    meta(file) {
      const date = file.createdAt ? new Date(file.createdAt).toLocaleDateString('ru-RU') : ''
      return [date, formatBytes(file.size)].filter(Boolean).join(' · ')
    },
    isPicked(file) {
      return this.picked.some(item => item.id === file.id)
    },
    toggle(file) {
      if (this.isPicked(file)) this.picked = this.picked.filter(item => item.id !== file.id)
      else this.picked = this.multiple ? [...this.picked, file] : [file]
    },
    pickNow(file) {
      if (this.multiple) return
      this.picked = [file]
      this.confirm()
      this.open = false
    },
    confirm() {
      if (this.picked.length) this.$emit('pick', this.picked)
    },
  },
}
</script>

<style lang="scss" scoped>
$line: #d8d6de;
$primary: #7367f0;

.file-library__tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.file-library__search {
  flex: 1;
  min-width: 220px;
}
.file-library__state {
  margin: 24px 0;
  text-align: center;
}
.file-library__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 12px;
}
.file-library__tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px;
  border: 2px solid transparent;
  border-radius: 8px;
  background: none;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: rgba($primary, 0.4);
    outline: none;
  }
  &.is-picked {
    border-color: $primary;
    background: rgba($primary, 0.08);
  }
}
.file-library__preview {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border: 1px solid $line;
  border-radius: 6px;
  background: rgba(#6e6b7b, 0.08);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
.file-library__icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: #6e6b7b;

  small {
    text-transform: uppercase;
  }
}
.file-library__check {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: $primary;
  color: #fff;
}
.file-library__name {
  overflow: hidden;
  font-size: 0.85rem;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.file-library__meta {
  font-size: 0.75rem;
  opacity: 0.7;
}
.file-library__more {
  display: flex;
  justify-content: center;
  min-height: 32px;
  margin-top: 16px;
}
</style>

<style lang="scss">
/* the modal's body is rendered outside the component: the list scrolls inside the window, the buttons stay in view */
.file-library__body {
  max-height: calc(100vh - 230px);
  overflow-y: auto;
}
</style>
